import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { AppThemeProvider } from './contexts/ThemeContext';
import { useAuth } from './hooks/useAuth';

// Layouts
import { MainLayout } from './layouts/MainLayout';
import { AdminLayout } from './layouts/AdminLayout';
import { ConsoleLayout } from './layouts/ConsoleLayout';

// Public Pages
import { AuthPage } from './pages/AuthPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';

// Admin Pages
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { UserManagementPage } from './pages/admin/UserManagementPage';
import { UserDetailAdminView } from './pages/admin/UserDetailAdminView';
import { StreamMonitorPage } from './pages/admin/StreamMonitorPage';
import { NocReportsPage } from './pages/admin/NocReportsPage';
import { SystemConfigPage } from './pages/admin/SystemConfigPage';
import { BillingAdminPage } from './pages/admin/BillingAdminPage';
import { FinancialReportsPage } from './pages/admin/FinancialReportsPage';
import { AdminReportsPage } from './pages/admin/AdminReportsPage';

const ServiceStatusPage = React.lazy(() => import('./pages/admin/ServiceStatusPage'));
const StreamInspectionPage = React.lazy(() => import('./pages/admin/StreamInspectionPage'));

type AllowedRole =
  | 'ADMIN'
  | 'TECHNICAL_ADMIN'
  | 'FINANCE_ADMIN'
  | 'SUPPORT_ADMIN'
  | 'AUDITOR'
  | 'BROADCASTER'
  | 'STREAMR';

const ADMIN_ROLES: AllowedRole[] = ['ADMIN', 'TECHNICAL_ADMIN', 'FINANCE_ADMIN', 'SUPPORT_ADMIN', 'AUDITOR'];
const CONSOLE_ROLES: AllowedRole[] = ['BROADCASTER', 'STREAMR'];

const ProtectedRoute: React.FC<{ children: React.ReactNode; roles: Array<AllowedRole> }> = ({ children, roles }) => {
  const { isLoggedIn, user } = useAuth();

  if (!isLoggedIn || !user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role as AllowedRole)) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

function AppContent() {
  return (
    <Routes>
      {/* Public entry points */}
      <Route element={<MainLayout />}>
        <Route path="/login" element={<AuthPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      </Route>

      {/* Admin console */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={ADMIN_ROLES}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<UserManagementPage />} />
        <Route path="users/:id" element={<UserDetailAdminView />} />
        <Route path="streams" element={<StreamMonitorPage />} />
        <Route path="streams/channels/:channelId" element={<React.Suspense fallback={<Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>}><StreamInspectionPage /></React.Suspense>} />
        <Route path="streams/reports" element={<NocReportsPage />} />
        <Route path="system" element={<SystemConfigPage />} />
        <Route path="billing" element={<BillingAdminPage />} />
        <Route path="billing/reports" element={<FinancialReportsPage />} />
        <Route path="reports" element={<AdminReportsPage />} />
        <Route path="status" element={<React.Suspense fallback={<Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>}><ServiceStatusPage /></React.Suspense>} />
        <Route path="mediamtx-settings" element={<Navigate to="/admin/system" replace />} />
      </Route>

      {/* User console aliases from the new src route order */}
      <Route path="/dashboard" element={<Navigate to="/console" replace />} />
      <Route path="/broadcaster" element={<Navigate to="/console" replace />} />
      <Route path="/streamr" element={<Navigate to="/console" replace />} />

      <Route
        path="/console/*"
        element={
          <ProtectedRoute roles={CONSOLE_ROLES}>
            <ConsoleLayout />
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AppThemeProvider>
      <CssBaseline />
      <SnackbarProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </SnackbarProvider>
    </AppThemeProvider>
  );
}

export default App;
